import { Action } from "redux";
import produce from "immer";
import {
  IDomain,
  ITenantInfo,
  ILandLordInfo,
  isLandlordTypeGuard,
  IDomainSettings,
} from "./models/userInfo";
import { IFavorite } from "./types";
import _ from "lodash";
import { DashboardTab } from "../tabsContainer/components/TabsContainer";

export interface ICommonState {
  currentUserInfo: ILandLordInfo | ITenantInfo;
  isFetching: boolean;
  selectedDomain: IDomain | null;
  favorites: IFavorite[];
  activeDashboardTab: DashboardTab;
  userIsReady: boolean;
  includeVAT: boolean;
}

const getFavoritesFromLocalStorage = (): IFavorite[] => {
  const favorites = localStorage.getItem("favorites");
  if (favorites && favorites.length > 0) {
    return JSON.parse(favorites);
  } else {
    return [];
  }
};

export const getDomainFromLocalStorage = (): IDomain | null => {
  const domainCode = localStorage.getItem("domainCode");
  if (domainCode && domainCode.length > 0) {
    return JSON.parse(domainCode);
  }
  return null;
};

const getActiveTabFromlocalStorage = (): DashboardTab => {
  const tab = localStorage.getItem("activeTab");
  if (!tab) return "Consumption";
  return tab as DashboardTab;
};

const getIncludeVATFromLocalStorage = (): boolean => {
  const VAT = localStorage.getItem("includeVAT");
  if (VAT === null) return true;
  return VAT === String(true);
};

const initialState: ICommonState = {
  currentUserInfo: {
    GivenName: "",
    SurName: "",
    Domains: [],
    NodeID: "",
    DomainSettings: {
      Currency: "SEK",
      TimeZone: "",
      DefaultCulture: "sv-SE",
      TenantAccessStatus: {
        canViewHours: false,
        canViewMeters: false,
        canViewTemperature: false,
        canViewBillingResults: false,
      },
    },
  },
  isFetching: false,
  selectedDomain: getDomainFromLocalStorage(),
  favorites: getFavoritesFromLocalStorage(),
  activeDashboardTab: getActiveTabFromlocalStorage(),
  userIsReady: false,
  includeVAT: getIncludeVATFromLocalStorage(),
};

const FETCHING = "COMMON/FETCHING";
const FETCHING_USER_INFO_SUCCESS = "COMMON/FETCHING_USER_INFO_SUCCESS";
const FETCHING_USER_INFO_ERROR = "COMMON/FETCHING_USER_INFO_ERROR";
const SET_SELECTED_DOMAIN = "COMMON/SET_SELECTED_DOMAIN";
const SET_FAVORITES = "COMMON/SET_FAVORITES";
const ADD_PHONENUMBER = "COMMON/ADD_PHONENUMBER";
const REMOVE_PHONENUMBER = "COMMON/REMOVE_PHONENUMBER";
const SET_DOMAIN_SETTINGS = "COMMON/SET_DOMAIN_SETTINGS";
const SET_ACTIVE_DASHBOARD_TAB = "COMMON/SET_ACTIVE_DASHBOARD_TAB";
const USER_IS_READY = "COMMON/USER_IS_READY";
const SET_INCLUDE_VAT = "COMMON/SET_INCLUDE_VAT";

export interface ISetActiveDashBoardTabAction extends Action {
  type: typeof SET_ACTIVE_DASHBOARD_TAB;
  payload: DashboardTab;
}

export interface IFetchingAction {
  type: typeof FETCHING;
}
export interface IFetchingUserInfoSuccessAction {
  type: typeof FETCHING_USER_INFO_SUCCESS;
  payload: ILandLordInfo | ITenantInfo;
}
export interface IFetchingUserInfoErrorAction {
  type: typeof FETCHING_USER_INFO_ERROR;
}
export interface ISetSelectedDomainAction {
  type: typeof SET_SELECTED_DOMAIN;
  payload: IDomain;
}
export interface ISetFavoritesAction {
  type: typeof SET_FAVORITES;
  payload: IFavorite[];
}

export interface ISetIncludeVATAction {
  type: typeof SET_INCLUDE_VAT;
  payload: boolean;
}

export interface ISetDomainSettingsAction {
  type: typeof SET_DOMAIN_SETTINGS;
  payload: IDomainSettings;
}

export interface IAddPhonenumberAction {
  type: typeof ADD_PHONENUMBER;
  payload: string;
}
export interface IRemovePhoneNumberAction {
  type: typeof REMOVE_PHONENUMBER;
  payload: string;
}

export interface IUserIsReadyAction {
  type: typeof USER_IS_READY;
}

export const actionCreator = {
  fetching: { type: FETCHING } as IFetchingAction,
  removePhoneNumber: (phoneNumber: string) =>
    ({
      type: REMOVE_PHONENUMBER,
      payload: phoneNumber,
    } as IRemovePhoneNumberAction),
  fetchingUserInfoSuccess: (userInfo: ITenantInfo | ILandLordInfo) =>
    ({
      type: FETCHING_USER_INFO_SUCCESS,
      payload: userInfo,
    } as IFetchingUserInfoSuccessAction),
  fetchingUserInfoError: {
    type: FETCHING_USER_INFO_ERROR,
  } as IFetchingUserInfoErrorAction,
  setPhoneNumber: (phoneNumber: string) =>
    ({
      type: ADD_PHONENUMBER,
      payload: phoneNumber,
    } as IAddPhonenumberAction),
  setSelectedDomain: (domain: IDomain) =>
    ({
      type: SET_SELECTED_DOMAIN,
      payload: domain,
    } as ISetSelectedDomainAction),
  setFavorites: (favorites: IFavorite[]) =>
    ({ type: SET_FAVORITES, payload: favorites } as ISetFavoritesAction),
  setDomainSettings: (payload: IDomainSettings) =>
    ({
      type: SET_DOMAIN_SETTINGS,
      payload: payload,
    } as ISetDomainSettingsAction),
  setActiveDashboardTab: (payload: DashboardTab) =>
    ({
      type: SET_ACTIVE_DASHBOARD_TAB,
      payload: payload,
    } as ISetActiveDashBoardTabAction),
  userIsReady: () =>
    ({
      type: USER_IS_READY,
    } as IUserIsReadyAction),
  setIncludeVAT: (payload: boolean) =>
    ({
      type: SET_INCLUDE_VAT,
      payload: payload,
    } as ISetIncludeVATAction),
};

export const commonReducer = (state = initialState, action: Action<string>) =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCHING:
        draft.isFetching = true;
        break;
      case FETCHING_USER_INFO_SUCCESS:
        const fetchingUserInfoSuccessAction =
          action as IFetchingUserInfoSuccessAction;
        draft.isFetching = false;
        draft.currentUserInfo = fetchingUserInfoSuccessAction.payload;
        draft.currentUserInfo.Domains = _.orderBy(
          draft.currentUserInfo.Domains,
          (d) => d.Name
        );
        break;
      case FETCHING_USER_INFO_ERROR:
        draft.isFetching = false;
        break;
      case SET_SELECTED_DOMAIN:
        const setSelectedDomainAction = action as ISetSelectedDomainAction;
        localStorage.setItem(
          "domainCode",
          JSON.stringify(setSelectedDomainAction.payload)
        );
        draft.selectedDomain = setSelectedDomainAction.payload;
        break;
      case SET_FAVORITES:
        const setFavoritesAction = action as ISetFavoritesAction;
        draft.favorites = setFavoritesAction.payload;
        break;
      case ADD_PHONENUMBER:
        const addPhoneNumberAction = action as IAddPhonenumberAction;
        //@ts-ignore
        if (!isLandlordTypeGuard(draft.currentUserInfo)) {
          draft.currentUserInfo.PhoneNumbers.push(addPhoneNumberAction.payload);
          draft.isFetching = false;
        }
        break;
      case REMOVE_PHONENUMBER:
        const removePhoneNumberAction = action as IRemovePhoneNumberAction;
        if (!isLandlordTypeGuard(draft.currentUserInfo)) {
          draft.currentUserInfo.PhoneNumbers =
            draft.currentUserInfo.PhoneNumbers.filter(
              (f) => f !== removePhoneNumberAction.payload
            ) ?? [];
        }
        break;
      case SET_DOMAIN_SETTINGS:
        const setDomainSettingsAction = action as ISetDomainSettingsAction;
        draft.currentUserInfo.DomainSettings = setDomainSettingsAction.payload;
        break;
      case SET_ACTIVE_DASHBOARD_TAB:
        const changeTabaction = action as ISetActiveDashBoardTabAction;
        draft.activeDashboardTab = changeTabaction.payload;
        localStorage.setItem("activeTab", changeTabaction.payload);
        break;
      case USER_IS_READY:
        draft.userIsReady = true;
        break;
      case SET_INCLUDE_VAT:
        const setIncludeVATAction = action as ISetIncludeVATAction;
        draft.includeVAT = setIncludeVATAction.payload;
    }
  });
